import { formatDate } from "utils/helpers";
import { RESULTS_PER_PAGE } from "constants";
import { apiSlice } from "features/api/apiSlice";
import { createEntityAdapter } from "@reduxjs/toolkit";

const callAccountAdapter = createEntityAdapter({});

const initialState = callAccountAdapter.getInitialState({ size: 0 });

export const callAccountApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCallAccounts: builder.query({
      query: (searchParams) =>
        `/call_accounts?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response.results)
          return callAccountAdapter.setAll(initialState, []);

        const fetchedCallAccounts = response.results.map((callAccount) => {
          const {
            enabled,
            domain_id,
            pricelist_id,
            created_by,
            created_at,
            updated_at,
            created_by_id,
            main_account_id,
            main_account,
            credit_limit,
            postpaid,
            pricelist,
            ...callAccountProps
          } = callAccount;

          callAccountProps["enabled"] = enabled ? "Yes" : "No";
          callAccountProps[
            "created_by"
          ] = `${created_by.lastname} ${created_by.firstname}`;

          callAccountProps["account"] = main_account?.number ?? ""
          // callAccountProps["pricelist"] = main_account?.pricelist ?? ""
          return {
            ...callAccountProps,
            created_at: formatDate(created_at),
            // updated_at: formatDate(updated_at),
          };
        });

        return callAccountAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedCallAccounts
        );
      },
      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "CallAccount", id })),
            { type: "CallAccount", id: "PARTIAL-LIST" },
          ]
          : [{ type: "CallAccount", id: "PARTIAL-LIST" }],
    }),
    getCallAccountsByUser: builder.query({
      query: ({ userId, searchParams }) =>
        `/users/${userId}/call_accounts?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response.results)
          return callAccountAdapter.setAll(initialState, []);

        const fetchedCallAccounts = response.results.map((callAccount) => {
          const {
            enabled,
            domain_id,
            pricelist_id,
            created_by,
            created_at,
            updated_at,
            created_by_id,
            main_account_id,
            main_account,
            postpaid,
            pricelist,
            ...callAccountProps
          } = callAccount;

          callAccountProps["enabled"] = enabled ? "Yes" : "No";
          callAccountProps["postpaid"] = postpaid ? "Yes" : "No";
          callAccountProps["pricelist"] = pricelist?.name || [];
          callAccountProps["main_account_number"] = main_account?.number || [];
          callAccountProps[
            "created_by"
          ] = `${created_by.lastname} ${created_by.firstname}`;

          return {
            ...callAccountProps,
            created_at: formatDate(created_at),
            // updated_at: formatDate(updated_at),
          };
        });

        return callAccountAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedCallAccounts
        );
      },
      providesTags: (result) =>
        result?.ids
          ? [
            ...result.ids.map((id) => ({ type: "CallAccount", id })),
            { type: "CallAccount", id: "PARTIAL-LIST" },
          ]
          : [{ type: "CallAccount", id: "PARTIAL-LIST" }],
    }),
    getCallAccount: builder.query({
      query: (id) => `/call_accounts/${id}`,
      providesTags: (result, error, id) => [{ type: "CallAccount", id }],
    }),
    createCallAccount: builder.mutation({
      query: (data) => ({
        url: "/call_accounts",
        method: "POST",
        body: JSON.stringify({ ...data }),
      }),
      invalidateTags: [{ type: "CallAccount", id: "PARTIAL-LIST" }],
    }),
    updateCallAccount: builder.mutation({
      query: ({ callAccountId, ...data }) => ({
        url: `/call_accounts/${callAccountId}`,
        method: "PATCH",
        body: JSON.stringify({
          id: callAccountId,
          ...data,
        }),
      }),
      invalidateTags: (result, error, args) => [
        { type: "CallAccount", id: args.id },
      ],
    }),
    deleteCallAccount: builder.mutation({ 
      query: (id) => ({
        url: `/call_accounts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CallAccount", id: arg.id },
        { type: "CallAccount", id: "PARTIAL-LIST" },
      ],
    }),
    creditCallAccount: builder.mutation({
      query: ({ accountId, ...data }) => ({
        url: `/call_accounts/${accountId}/transactions`,
        method: "POST",
        body: JSON.stringify({ ...data }),
      }),
      invalidateTags: (result, error, args) => [
        { type: "CallAccount", id: args.id },
      ],
    }),
  }),
});

export const {
  useGetCallAccountQuery,
  useGetCallAccountsQuery,
  useCreditCallAccountMutation,
  useCreateCallAccountMutation,
  useDeleteCallAccountMutation,
  useUpdateCallAccountMutation,
  useGetCallAccountsByUserQuery
} = callAccountApiSlice;
